import { useEffect, useMemo, useState } from 'react';
import api from '../api/client';
import OfficialSolutionsPanel from './OfficialSolutionsPanel';
import ReadOnlyPythonCode from './ReadOnlyPythonCode';
import { showsAnswerBlank } from '../utils/baseProgRole';
import { taskNavRef } from '../utils/taskNav';

function hasWorkspaceState(ws) {
  if (!ws || typeof ws !== 'object') return false;
  return Object.keys(ws).length > 0;
}

function attemptPayload(attempt) {
  if (!attempt) return null;
  const code = String(attempt.code_snapshot ?? '').trim();
  const answer = String(attempt.tests_result?.student_text_answer ?? '').trim();
  const workspaceOnly = !code && !answer && hasWorkspaceState(attempt.workspace_state);
  if (!code && !answer && !workspaceOnly) return null;
  return {
    studentId: attempt.student,
    at: attempt.created_at || '',
    code,
    answer,
    workspaceOnly,
  };
}

function draftPayload(draft) {
  if (!draft) return null;
  const code = String(draft.code ?? '').trim();
  const answer = String(draft.student_answer ?? '').trim();
  const workspaceOnly = !code && !answer && Boolean(draft.has_workspace);
  if (!code && !answer && !workspaceOnly) return null;
  return {
    studentId: draft.student_id,
    at: draft.updated_at || '',
    code,
    answer,
    workspaceOnly,
  };
}

function collapseKey(item) {
  if (item.workspaceOnly) return '__workspace__';
  if (item.code) return `code:${item.code}`;
  return `answer:${item.answer}`;
}

/**
 * Из попыток и черновиков: один последний непустой текст на ученика,
 * затем одинаковые тексты схлопываются.
 */
export function buildCollapsedStudentSolutions(attemptsData) {
  if (!attemptsData) return [];
  const byStudent = new Map();

  for (const group of attemptsData.students || []) {
    const attempts = Array.isArray(group.attempts) ? group.attempts : [];
    for (const attempt of attempts) {
      const payload = attemptPayload(attempt);
      if (!payload) continue;
      const sid = group.student_id ?? payload.studentId;
      const prev = byStudent.get(sid);
      // attempts уже отсортированы newest-first; берём первую непустую
      if (!prev) byStudent.set(sid, payload);
      break;
    }
  }

  for (const draft of attemptsData.drafts || []) {
    const payload = draftPayload(draft);
    if (!payload) continue;
    const sid = payload.studentId;
    const prev = byStudent.get(sid);
    if (!prev || String(payload.at) > String(prev.at)) {
      byStudent.set(sid, payload);
    }
  }

  const buckets = new Map();
  for (const item of byStudent.values()) {
    const key = collapseKey(item);
    const existing = buckets.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      buckets.set(key, { ...item, count: 1 });
    }
  }

  return Array.from(buckets.values()).sort((a, b) => b.count - a.count || String(b.at).localeCompare(String(a.at)));
}

function ruSame(n) {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return `${n} одинаковое`;
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return `${n} одинаковых`;
  return `${n} одинаковых`;
}

/**
 * Кнопка «Решения» на карточке банка: эталон + как решали ученики (без имён).
 * Данные грузятся только при открытии.
 */
export default function TaskBankSolutionsPeek({ task }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [taskFull, setTaskFull] = useState(null);
  const [attemptsData, setAttemptsData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const taskId = task?.id;
  const ref = task ? taskNavRef(task) : '';

  useEffect(() => {
    setOpen(false);
    setLoaded(false);
    setTaskFull(null);
    setAttemptsData(null);
    setError('');
  }, [taskId]);

  useEffect(() => {
    if (!open || loaded || !taskId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');

    const editorRef = ref || String(taskId);

    Promise.all([
      api.get(`/tasks/editor/${editorRef}/`),
      api.get(`/teacher/tasks/${taskId}/attempts/`),
    ])
      .then(([editorRes, attemptsRes]) => {
        if (cancelled) return;
        setTaskFull(editorRes.data || null);
        setAttemptsData(attemptsRes.data || null);
        setLoaded(true);
      })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Не удалось загрузить решения.');
        setTaskFull(null);
        setAttemptsData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, loaded, taskId, ref]);

  const collapsed = useMemo(
    () => buildCollapsedStudentSolutions(attemptsData),
    [attemptsData],
  );

  const expectedAnswer = String(taskFull?.expected_answer ?? '').trim();
  const showBlankAnswer = expectedAnswer && showsAnswerBlank(expectedAnswer);

  return (
    <div className="task-bank-solutions-peek">
      <button
        type="button"
        className={`btn btn-outline btn-sm task-bank-solutions-peek-btn${open ? ' is-on' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? 'Скрыть решения' : 'Решения'}
      </button>
      {open ? (
        <div className="task-bank-solutions-peek-panel">
          {loading ? (
            <p className="text-secondary">Загрузка решений…</p>
          ) : null}
          {error ? <p className="alert alert-error">{error}</p> : null}
          {!loading && !error ? (
            <>
              <section className="task-bank-solutions-peek-section">
                <h4 className="task-bank-solutions-peek-heading">Эталон</h4>
                {taskFull ? (
                  <div className="task-bank-solutions-peek-etalon">
                    {showBlankAnswer ? (
                      <p>
                        <strong>Ожидаемый ответ:</strong>{' '}
                        <code className="task-bank-solutions-peek-answer">{expectedAnswer}</code>
                      </p>
                    ) : !expectedAnswer ? (
                      <p className="text-secondary">Ожидаемый ответ не задан.</p>
                    ) : null}
                    <OfficialSolutionsPanel task={taskFull} />
                    {!String(taskFull.solution_code ?? '').trim()
                    && !String(taskFull.solution_explanation ?? '').trim()
                    && !(Array.isArray(taskFull.alternative_solutions) && taskFull.alternative_solutions.length) ? (
                      <p className="text-secondary">Эталонного кода и разбора нет.</p>
                    ) : null}
                  </div>
                ) : (
                  <p className="text-secondary">Не удалось загрузить эталон.</p>
                )}
              </section>

              <section className="task-bank-solutions-peek-section">
                <h4 className="task-bank-solutions-peek-heading">Как решали</h4>
                {collapsed.length === 0 ? (
                  <p className="text-secondary">Попыток и черновиков пока нет.</p>
                ) : (
                  <ul className="task-bank-solutions-peek-list">
                    {collapsed.map((item, idx) => (
                      <li key={`${collapseKey(item)}-${idx}`} className="task-bank-solutions-peek-item">
                        {item.count > 1 ? (
                          <p className="task-bank-solutions-peek-count text-secondary">
                            {ruSame(item.count)}
                          </p>
                        ) : null}
                        {item.workspaceOnly ? (
                          <p className="text-secondary">Есть чертёж или таблица (без кода).</p>
                        ) : null}
                        {item.answer && !item.code ? (
                          <p>
                            <strong>Ответ:</strong>{' '}
                            <code className="task-bank-solutions-peek-answer">{item.answer}</code>
                          </p>
                        ) : null}
                        {item.code ? (
                          <ReadOnlyPythonCode value={item.code} />
                        ) : null}
                        {item.code && item.answer ? (
                          <p className="text-secondary">
                            Ответ в бланке: <code>{item.answer}</code>
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
                {attemptsData?.truncated ? (
                  <p className="text-secondary">
                    Показаны не все попытки (лимит {attemptsData.count}).
                  </p>
                ) : null}
              </section>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
